import { useTranslation } from 'react-i18next';

import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger, Icon, cn } from '@/design-system';
import { SUPPORTED_LOCALES, localeDefinition } from '@/shared/i18n';
import { selectLocale, selectLocaleSignedOut } from '@/shared/preferences';

export interface LanguageMenuProps {
  /**
   * `profile` (signed in): the choice is saved in this browser and on the user's profile.
   * `browser` (sign-in pages): saved in this browser only; it reaches the profile after sign-in if the profile has none.
   */
  persist: 'profile' | 'browser';
  className?: string;
}

/**
 * Compact language control (28px toolbar size): globe + the current language's short label, and a menu of every
 * language in the registry, each named in its own language. Switching applies in place: no reload, no sign-out.
 */
export function LanguageMenu({ persist, className }: LanguageMenuProps) {
  const { t, i18n } = useTranslation();
  const current = localeDefinition(i18n.resolvedLanguage ?? i18n.language);
  const select = (code: string) => void (persist === 'profile' ? selectLocale(code) : selectLocaleSignedOut(code));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('language.label')}
          className={cn('flex h-7 items-center gap-1.5 rounded-sm px-2 text-label text-ink-secondary hover:bg-surface-muted hover:text-ink', className)}
        >
          <Icon name="action.language" size="xs" />
          <span lang={current.pseudo ? undefined : current.code} aria-hidden="true">
            {current.shortName}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-40">
        <DropdownMenuRadioGroup value={current.code} onValueChange={select}>
          {SUPPORTED_LOCALES.map((l) => (
            <DropdownMenuRadioItem key={l.code} value={l.code}>
              <span lang={l.code} dir={l.dir}>
                {l.name}
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
